// ═══════════════════════════════════════════════════════════
// ARAMS UI — Button Component
// Variants: primary, secondary, danger, ghost, outline, brand-gradient
// Sizes: xs, sm, md, lg, icon
// ═══════════════════════════════════════════════════════════

import { forwardRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-medium rounded-lg transition-all duration-200 whitespace-nowrap ring-offset-surface-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary:
          'bg-brand-600 text-white hover:bg-brand-500 shadow-lg shadow-brand-600/25 active:shadow-md',
        secondary:
          'bg-surface-800 text-surface-200 border border-surface-700 hover:bg-surface-700 hover:border-surface-600',
        danger:
          'bg-danger/10 text-danger border border-danger/30 hover:bg-danger/20 active:bg-danger/30',
        ghost:
          'text-surface-400 hover:text-surface-200 hover:bg-surface-800/50',
        outline:
          'border border-surface-700 text-surface-300 hover:bg-surface-800/60 hover:border-surface-600',
        'brand-gradient':
          'bg-gradient-to-r from-brand-600 to-purple-600 text-white hover:from-brand-500 hover:to-purple-500 shadow-lg shadow-brand-600/25',
        link: 'text-brand-400 hover:text-brand-300 underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        xs: 'h-7 px-2.5 text-xs rounded-md',
        sm: 'h-8 px-3 text-xs',
        md: 'h-10 px-4 text-sm',
        lg: 'h-12 px-6 text-base',
        icon: 'h-10 w-10',
        'icon-sm': 'h-8 w-8',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  asChild?: boolean;
}

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, asChild = false, children, disabled, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size }), className)}
        ref={ref}
        disabled={disabled || loading}
        {...props}
      >
        {loading && <Loader2 className="w-4 h-4 animate-spin" />}
        {children}
      </Comp>
    );
  },
);

Button.displayName = 'Button';
export { Button, buttonVariants };
